/**
 * The student's assignment list — `/assignments`.
 *
 * Ordered by the server soonest-deadline-first, undated last, which is the order a student actually
 * needs. Rows needing revision are lifted to the top of the rendered list on top of that: a returned
 * assignment is the one piece of work where someone is explicitly waiting on them, and burying it
 * behind an earlier deadline they have already submitted for would hide the correction loop.
 *
 * `canSubmit` / `cannotSubmitReason` come from the server — the same policy the submit endpoint
 * enforces — so this page never has to re-derive whether a deadline or an attempt cap has bitten.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { CardSkeleton } from "@/components/loading";
import { PaginationControls } from "@/components/PaginationControls";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { useMyAssignments } from "@/hooks/use-assignments";
import { SubmissionStatusBadge } from "@/components/assignments/submission-status-badge";
import {
  SubmissionStatus,
  type StudentAssignment,
  type StudentAssignmentPerformance,
} from "@/lib/api/assignments";
import {
  AlertTriangle,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  Lock,
  Paperclip,
  RotateCcw,
  Trophy,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/assignments")({
  component: MyAssignmentsPage,
});

function formatDue(iso: string | null): string {
  if (!iso) return "No deadline";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "No deadline" : date.toLocaleString();
}

/**
 * The modules an assignment covers, as a suffix for the course line. Names one module, and counts
 * the rest — "Intro to CSS +2 more" stays readable where four full titles would wrap the card.
 * Falls back to `moduleTitle` for an API build that predates multi-module coverage.
 */
function describeCoverage(assignment: StudentAssignment): string {
  const covered = assignment.modules ?? [];
  if (covered.length === 0) return assignment.moduleTitle ? ` · ${assignment.moduleTitle}` : "";
  if (covered.length === 1) return ` · ${covered[0].title}`;
  return ` · ${covered[0].title} +${covered.length - 1} more`;
}

/** One decimal place, but never a bare ".0" — "85%" reads better than "85.0%". */
function formatPercent(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * The student's standing across every assignment delivered to them, which is NOT what the rows on
 * this page add up to: the aggregate spans the whole applicable set while the list is one page of it.
 *
 * The points and the percentage cover the GRADED assignments only — the same rule the server
 * applies. Showing "680 / 800" against every assignment ever published would read as a collapsing
 * score each time new homework arrives, when nothing about the student's work had changed.
 */
function PerformanceSummary({ performance }: { performance: StudentAssignmentPerformance }) {
  const hasScore = performance.gradedAssignmentCount > 0 && performance.possiblePoints > 0;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-4">
        <div className="flex items-center gap-3">
          <Trophy className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-hidden />
          <div>
            <p className="text-sm font-medium">Assignment performance</p>
            <p className="text-xs text-muted-foreground">
              {performance.gradedAssignmentCount} / {performance.applicableAssignmentCount} graded
              {performance.pendingAssignmentCount > 0 &&
                ` · ${performance.pendingAssignmentCount} not marked yet`}
            </p>
          </div>
        </div>

        {hasScore ? (
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-semibold">
              {performance.earnedPoints} / {performance.possiblePoints}
            </span>
            <Badge
              variant="outline"
              className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
            >
              {formatPercent(performance.scorePercentage)}%
            </Badge>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing graded yet — your score appears once a reviewer marks your first assignment.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function MyAssignmentsPage() {
  const paging = usePaginationState({ initialPageSize: 10 });
  const params = useMemo(
    () => ({ pageNumber: paging.pageNumber, pageSize: paging.pageSize }),
    [paging.pageNumber, paging.pageSize],
  );
  const { data, isLoading, isError, error, isFetching } = useMyAssignments(params);

  // Stable sort that only lifts the "waiting on you" rows; the server's deadline order is preserved
  // inside each group.
  const rows = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort((a, b) => Number(b.needsRevision) - Number(a.needsRevision));
  }, [data]);

  const needsRevisionCount = rows.filter((r) => r.needsRevision).length;

  // Hidden with nothing to describe: a "0 / 0 graded" card on an empty page is noise, and the empty
  // state below already says why there is nothing here. Absent entirely on an API build that
  // predates the aggregate, which is the same "nothing to say" case.
  const performance = data?.performance;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Assignments</h1>
        <p className="text-sm text-muted-foreground">
          Your homework: read the brief, submit your work, and act on your reviewer&apos;s
          corrections.
        </p>
      </div>

      {performance && performance.applicableAssignmentCount > 0 && (
        <PerformanceSummary performance={performance} />
      )}

      {needsRevisionCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3">
          <RotateCcw
            className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400"
            aria-hidden
          />
          <p className="text-sm">
            <span className="font-medium">
              {needsRevisionCount} assignment{needsRevisionCount === 1 ? "" : "s"} returned for
              revision.
            </span>{" "}
            <span className="text-muted-foreground">
              Read the comments, then submit a revision — your previous attempt is kept.
            </span>
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Could not load your assignments."}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No assignments yet"
          description="When a team leader publishes an assignment on one of your courses, it appears here."
        />
      ) : (
        <>
          <div className="space-y-3">
            {rows.map((assignment) => (
              <Card
                key={assignment.id}
                className={
                  assignment.needsRevision
                    ? "border-indigo-500/40"
                    : assignment.isOverdue
                      ? "border-orange-500/40"
                      : undefined
                }
              >
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold">{assignment.title}</h2>
                        <SubmissionStatusBadge status={assignment.myStatus} />
                        {assignment.isOverdue && (
                          <Badge
                            variant="outline"
                            className="gap-1 border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-400"
                          >
                            <AlertTriangle className="h-3 w-3" aria-hidden />
                            Overdue
                          </Badge>
                        )}
                        {/* Only while it is still blocking. Acceptance is the trigger, so this stays
                            up through review and clears when the work is graded — not when it is
                            handed in. */}
                        {assignment.locksLaterModules &&
                          assignment.myStatus !== SubmissionStatus.Graded && (
                            <Badge
                              variant="outline"
                              className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                            >
                              <Lock className="h-3 w-3" aria-hidden />
                              Unlocks the next modules
                            </Badge>
                          )}
                        {assignment.isLocked && (
                          <Badge
                            variant="outline"
                            className="gap-1 border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-300"
                          >
                            <Lock className="h-3 w-3" aria-hidden />
                            Locked
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="mt-1">
                        {assignment.courseName}
                        {describeCoverage(assignment)}
                      </CardDescription>
                    </div>
                    <Button
                      asChild
                      size="sm"
                      variant={assignment.needsRevision ? "default" : "outline"}
                    >
                      <Link
                        to="/assignments/$assignmentId"
                        params={{ assignmentId: assignment.id }}
                      >
                        {assignment.needsRevision
                          ? "View corrections"
                          : assignment.isLocked
                            ? "See what's left"
                            : assignment.myAttemptCount === 0
                              ? "Open & submit"
                              : "View"}
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                      {formatDue(assignment.dueDate)}
                    </span>
                    {assignment.attachmentCount > 0 && (
                      <span className="inline-flex items-center gap-1.5">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden />
                        {assignment.attachmentCount} in the brief
                      </span>
                    )}
                    <span>
                      {assignment.myGrade !== null
                        ? `Grade ${assignment.myGrade} / ${assignment.pointsPossible}`
                        : `${assignment.pointsPossible} points`}
                    </span>
                    {assignment.myAttemptCount > 0 && (
                      <span>
                        {assignment.myAttemptCount} attempt
                        {assignment.myAttemptCount === 1 ? "" : "s"}
                        {assignment.maxAttempts && ` of ${assignment.maxAttempts}`}
                      </span>
                    )}
                  </div>

                  {/* The server's own refusal text, so the reason a student cannot submit is the same
                      sentence the submit endpoint would have answered with. */}
                  {!assignment.canSubmit &&
                    assignment.cannotSubmitReason &&
                    assignment.myStatus !== SubmissionStatus.Graded && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        {assignment.cannotSubmitReason}
                      </p>
                    )}
                </CardContent>
              </Card>
            ))}
          </div>

          <PaginationControls
            pageNumber={data!.pageNumber}
            pageSize={data!.pageSize}
            totalPages={data!.totalPages}
            totalCount={data!.totalCount}
            hasNextPage={data!.hasNextPage}
            hasPreviousPage={data!.hasPreviousPage}
            onPageChange={paging.setPageNumber}
            onPageSizeChange={paging.setPageSize}
            isLoading={isFetching}
          />
        </>
      )}
    </div>
  );
}
